<template>
  <div>
    <div
      v-if="!will_register"
      class="w-full p-2 mx-auto bg-white border border-gray-300 shadow sm:rounded-lg sm:p-4 sm:w-11/12 md:w-5/6"
    >
      <div class="flex flex-col items-center space-y-4 font-semibold">
        <p>It looks as though you have not visited TVAP before.</p>
        <p>Would you like to register with us?</p>
        <div class="flex items-center justify-center pt-4 space-x-8">
          <button
            class="px-3 py-2 text-base font-medium text-white duration-300 transform bg-green-500 rounded shadow w-28 hover:opacity-80"
            @click="will_register = true"
          >
            Yes please
          </button>
          <a
            :href="retURL"
            class="inline-block text-sm text-center text-gray-500 underline w-28 hover:text-opacity-80 hover:opacity-70"
            >No thanks</a
          >
        </div>
      </div>
    </div>
    <div
      v-else
      class="w-full mx-auto bg-white border border-gray-300 shadow sm:rounded-lg sm:w-11/12 md:w-5/6"
    >
      <h1
        class="p-2 text-sm font-semibold tracking-wide text-gray-800 sm:text-base sm:p-4"
      >
        Please enter the following information and we will call you to complete
        the registration process.
      </h1>

      <!-- -------------------------------------------------------------------------  -->
      <!-- production org                                                             -->
      <!-- https://webto.salesforce.com/servlet/servlet.WebToLead                     -->
      <!-- name="oid" value="00D1r000001o8VI"                                         -->
      <!-- -------------------------------------------------------------------------  -->

      <form
        ref="w2lform"
        :action="formSubmitAction"
        method="POST"
        class="p-2 sm:p-4"
      >
        <input type="hidden" name="oid" :value="salesforce.oid" />
        <input type="hidden" name="retURL" :value="retURL" />
        <input type="hidden" name="company" value="Individual" />
        <input type="hidden" name="state" :value="county" />
        <input type="hidden" name="email" :value="email" />
        <input
          :id="salesforce.date"
          type="hidden"
          :name="salesforce.date"
          :value="booking.date"
        />
        <input
          :id="salesforce.users"
          type="hidden"
          :name="salesforce.users"
          :value="booking.users"
        />

        <!--  ----------------------------------------------------------------------  -->
        <!--  NOTE: These fields are optional debugging elements. Please uncomment    -->
        <!--  these lines if you wish to test in debug mode.                          -->
        <!-- <input type="hidden" name="debug" value="1" /> -->
        <!-- <input type="hidden" name="debugEmail" value="ian@exegi.software" /> -->
        <!--  ----------------------------------------------------------------------  -->

        <div
          class="flex flex-col items-center justify-center w-full sm:space-x-6 sm:flex-row"
        >
          <div class="flex flex-col w-full my-2">
            <label for="first_name" class="text-sm font-medium text-gray-700"
              >First Name</label
            ><input
              id="first_name"
              v-model="firstname"
              maxlength="40"
              name="first_name"
              type="text"
              class="w-full border-gray-300 rounded shadow focus:outline-none focus:ring-2 focus:ring-indigo-300"
              :class="{
                'ring-2 ring-red-300 border-red-300': firstNameError,
              }"
            />
          </div>

          <div class="flex flex-col w-full my-2">
            <label for="last_name" class="text-sm font-medium text-gray-700"
              >Last Name</label
            ><input
              id="last_name"
              v-model="lastname"
              maxlength="80"
              name="last_name"
              type="text"
              class="w-full border-gray-300 rounded shadow"
              :class="{ 'ring-2 ring-red-300 border-red-300': lastNameError }"
            />
          </div>
        </div>

        <div
          class="flex flex-col items-center justify-center sm:space-x-6 sm:flex-row"
        >
          <div class="flex flex-col w-full my-2">
            <label for="phone" class="text-sm font-medium text-gray-700"
              >Mobile number</label
            ><input
              id="mobile"
              v-model="phone"
              maxlength="40"
              name="mobile"
              type="text"
              class="w-full border-gray-300 rounded shadow"
              :class="{ 'ring-2 ring-red-300 border-red-300': phoneError }"
            />
          </div>

          <div class="flex flex-col w-full my-2">
            <label for="postcode" class="text-sm font-medium text-gray-700"
              >Postcode</label
            ><input
              id="zip"
              v-model="postcode"
              maxlength="10"
              name="zip"
              type="text"
              class="w-full border-gray-300 rounded shadow"
              :class="{ 'ring-2 ring-red-300 border-red-300': postcodeError }"
            />
          </div>
        </div>

        <div class="flex flex-col w-full my-2 space-y-2">
          <div class="flex items-center text-sm font-medium text-gray-700">
            <span class="inline-block w-5/6 lg:w-11/12"
              >Does your booking include one or more people with SEN?</span
            >
            <input
              :id="salesforce.sen"
              :name="salesforce.sen"
              type="checkbox"
              value="1"
              class="focus:outline-none focus:ring-2 focus:ring-indigo-300"
            />
          </div>

          <div class="flex items-center text-sm font-medium text-gray-700">
            <span class="inline-block w-5/6 lg:w-11/12"
              >Are you booking for one or more people children (under 17)?</span
            >
            <input
              :id="salesforce.u17"
              :name="salesforce.u17"
              type="checkbox"
              value="1"
              class="focus:outline-none focus:ring-2 focus:ring-indigo-300"
            />
          </div>
        </div>

        <div class="flex items-center mt-4 text-sm font-medium text-gray-700">
          <span>How did you hear about us?</span>
          <select
            :id="salesforce.how"
            v-model="how"
            :name="salesforce.how"
            title="How did you hear about us?"
            class="w-1/3 p-0 px-2 py-1 ml-4 text-sm outline-none focus:outline-none"
            :class="{ 'ring-2 ring-red-300 border-red-300': howError }"
            ><option value="">-- Please select --</option
            ><option value="Friend">Friend</option>
            <option value="School">School</option>
            <option value="Local Authority">Local Authority</option>
            <option value="TVAP event">TVAP event</option>
            <option value="Local Offer site">Local Offer site</option>
            <option value="TVAP Website">TVAP Website</option>
            <option value="TVAP social media">TVAP social media</option>
            <option value="Local Press">Local Press</option>
            <option value="GP/Health Professional"
              >GP/Health Professional</option
            >
          </select>
        </div>

        <div
          v-if="error.length"
          class="mt-2 font-semibold text-center text-red-600 md:mt-4"
        >
          Please enter all fields
        </div>

        <!-- Tell Salesforce this came from the bookings app -->
        <input id="lead_source" name="lead_source" type="hidden" value="App" />

        <div
          class="flex items-center justify-center mt-4 mb-2 space-x-6 md:mt-6"
        >
          <button
            class="px-4 py-2 text-white rounded-md shadow focus:outline-none bg-tvap-green hover:opacity-80"
            @click.prevent="submit"
          >
            Submit
          </button>
          <a
            :href="retURL"
            class="text-sm text-gray-500 underline lowercase hover:text-opacity-80"
            >cancel</a
          >
        </div>
      </form>
      <footer
        class="block w-full p-2 mt-2 text-xs font-normal bg-gray-100 border-t border-gray-200 sm:mt-4"
      >
        <span
          >By submitting this account request I give my permission for TVAP to
          contact me solely in order to complete my registration to access
          online booking requests.</span
        >
        <br />
        <span
          >To view our GDPR policies please visit
          <a href="https://tvap.co.uk" class="underline">tvap.co.uk</a></span
        >
      </footer>
    </div>
  </div>
</template>

<script>
import { http } from '@/lib';
import { isValid } from 'postcode';
import moment from 'moment';
import stateProps from '@/components/mixins/state-props';

export default {
  mixins: [stateProps],

  data() {
    return {
      booking: {
        date: moment(this.current.context.booking.date).format('DD/MM/YYYY'),
        users: this.current.context.booking.users,
      },

      // Populated by a lookup on user entered postcode
      county: '',

      // The email used to attempt registration - noinput at this stage
      email: this.current.context.userid,

      // UI fields
      error: [],
      firstname: '',
      how: '',
      lastname: '',
      phone: '',
      postcode: '',

      // unique identifiers for custom fields in SF
      salesforce: {
        date: process.env.VUE_APP_SALESFORCE_DATE,
        how: process.env.VUE_APP_SALESFORCE_HOW,
        oid: process.env.VUE_APP_SALESFORCE_OID,
        u17: process.env.VUE_APP_SALESFORCE_U17,
        sen: process.env.VUE_APP_SALESFORCE_SEN,
        users: process.env.VUE_APP_SALESFORCE_USERS,
      },

      will_register: false,
    };
  },

  computed: {
    formSubmitAction() {
      return process.env.VUE_APP_SALESFORCE_URL;
    },
    firstNameError() {
      return this.error.includes('firstname');
    },
    howError() {
      return this.error.includes('how');
    },
    lastNameError() {
      return this.error.includes('lastname');
    },
    phoneError() {
      return this.error.includes('phone');
    },
    postcodeError() {
      return this.error.includes('postcode');
    },
    retURL() {
      return process.env.VUE_APP_RET_URL;
    },
  },

  methods: {
    async submit() {
      const valid = await this.valid();
      if (valid) {
        const form = this.$refs['w2lform'];
        form.submit();
      }
    },

    async valid() {
      this.error = [];
      if (!this.firstname) {
        this.error.push('firstname');
      }
      if (!this.how) {
        this.error.push('how');
      }
      if (!this.lastname) {
        this.error.push('lastname');
      }
      if (!this.phone) {
        this.error.push('phone');
      }
      if (!this.postcode || !isValid(this.postcode)) {
        this.error.push('postcode');
      }
      if (!this.error.length) {
        const postcode = this.postcode.replace(/\s/g, '');
        const location = await http.get(`/postcodes/${postcode}`);
        if (location.data.success) {
          this.county = location.data.data.county;
        } else {
          this.error.push('postcode');
        }
      }
      return this.error.length > 0 ? false : true;
    },
  },
};
</script>
